import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import "./ProfitLineChart.css";

const months = [
  { month: "Jan", value: "1" },
  { month: "Feb", value: "2" },
  { month: "Mar", value: "3" },
  { month: "Apr", value: "4" },
  { month: "May", value: "5" },
  { month: "Jun", value: "6" },
  { month: "Jul", value: "7" },
  { month: "Aug", value: "8" },
  { month: "Sep", value: "9" },
  { month: "Oct", value: "10" },
  { month: "Nov", value: "11" },
  { month: "Dec", value: "12" },
];

// Helper function to format y-axis values
const formatYAxisValue = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  } else if (value === 0) {
    return "$0";
  }
  return `$${value}`;
};

const computeYAxis = (data) => {
  if (!data || data.length === 0) {
    return {
      yDomain: [0, 100],
      yTicks: [0, 20, 40, 60, 80, 100],
    };
  }

  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.budget || 0, d.actual || 0))
  );

  // If everything is 0, show a small range so the axis is visible
  if (maxValue === 0) {
    return {
      yDomain: [0, 100],
      yTicks: [0, 20, 40, 60, 80, 100],
    };
  }

  // Nice tick calculation (about 5–6 ticks from 0 to max)
  const roughStep = maxValue / 5;
  const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / pow10;

  let niceNormalized;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;

  const step = niceNormalized * pow10;
  const maxTick = Math.ceil(maxValue / step) * step;

  const ticks = [];
  for (let v = 0; v <= maxTick; v += step) {
    ticks.push(v);
  }

  return {
    yDomain: [0, maxTick],
    yTicks: ticks,
  };
};

// Custom styles for the time controls
const controlContainerStyle = {
  position: "absolute",
  top: "20px",
  right: "20px",
  zIndex: 10,
  backgroundColor: "white",
  padding: "8px 12px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: "1px solid #e0e0e0",
  minWidth: "140px",
  textAlign: "center",
};

const controlStyle = {
  border: "none",
  outline: "none",
  fontSize: "14px",
  color: "#102a3d",
  backgroundColor: "transparent",
  cursor: "pointer",
  fontWeight: "500",
  width: "100%",
  textAlign: "center",
  fontFamily: "inherit",
};

// Year-only calendar selector component
const YearCalendarSelector = ({ selectedYear, onYearChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const [allYears, setAllYears] = useState([]);
  const [displayedYears, setDisplayedYears] = useState([]);

  // Generate years from currentYear-100 to currentYear+10
  useEffect(() => {
    const yearsArray = [];
    for (let year = currentYear - 100; year <= currentYear + 10; year++) {
      yearsArray.push(year);
    }
    setAllYears(yearsArray);
    setDisplayedYears(yearsArray);
  }, [currentYear]);

  const handleYearClick = (year) => {
    onYearChange(year.toString());
    setIsOpen(false);
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setDisplayedYears(allYears);
    } else {
      const filteredYears = allYears.filter((year) =>
        year.toString().includes(searchTerm)
      );
      setDisplayedYears(filteredYears);
    }
  };

  return (
    <div
      style={{
        ...controlContainerStyle,
        minWidth: "100px",
        cursor: "pointer",
        position: "relative",
        top: "-10px",
        right: "-330px",
        width: "30%",
      }}
    >
      <div
        onClick={toggleCalendar}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 8px",
        }}
      >
        <span style={{ color: selectedYear ? "#102a3d" : "#999" }}>
          {selectedYear || "Select Year"}
        </span>
        <span style={{ fontSize: "12px", color: "#102a3d" }}>▼</span>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 100,
            marginTop: "4px",
          }}
        >
          <div
            style={{
              padding: "8px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e0e0e0",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <input
              type="text"
              placeholder="Search year..."
              style={{
                width: "100%",
                padding: "4px 8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "12px",
              }}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div
            style={{
              maxHeight: "150px",
              overflowY: "auto",
            }}
          >
            {displayedYears.map((year) => (
              <div
                key={year}
                onClick={() => handleYearClick(year)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  backgroundColor:
                    selectedYear === year.toString() ? "#e3f2fd" : "white",
                  color:
                    selectedYear === year.toString() ? "#1976d2" : "#102a3d",
                  fontSize: "14px",
                  fontWeight: selectedYear === year.toString() ? "600" : "400",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    selectedYear === year.toString() ? "#e3f2fd" : "white";
                }}
              >
                {year}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Week selector component
const WeekSelector = ({ selectedWeek, onWeekChange }) => {
  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);

  return (
    <div style={controlContainerStyle}>
      <select
        value={selectedWeek}
        onChange={(e) => onWeekChange(e.target.value)}
        style={controlStyle}
      >
        <option value="">Select Week</option>
        {weeks.map((week) => (
          <option key={week} value={week}>
            Week {week}
          </option>
        ))}
      </select>
    </div>
  );
};

// Month selector component
const MonthSelector = ({ selectedMonth, onMonthChange }) => {
  return (
    <div style={controlContainerStyle}>
      <select
        value={selectedMonth}
        onChange={(e) => onMonthChange(e.target.value)}
        style={controlStyle}
      >
        <option value="">Select Month</option>
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.month}
          </option>
        ))}
      </select>
    </div>
  );
};

// Date picker component
const DatePicker = ({ selectedDate, onDateChange }) => {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div style={controlContainerStyle}>
      <input
        type="date"
        value={selectedDate}
        onChange={(e) => onDateChange(e.target.value)}
        style={controlStyle}
        max={today}
      />
    </div>
  );
};

const ProfitLineChart = ({ data, time, setFilter }) => {
  // State for each time selector with default values
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // Clear other selections when time changes
  useEffect(() => {
    setSelectedDate("");
    setSelectedWeek("");
    setSelectedMonth("");
    setSelectedYear("");
    // Clear parent selection when time changes
    if (setFilter) {
      setFilter({ day: "", week: "", month: "", year: "" });
    }
  }, [time, setFilter]);

  // Update parent's filter whenever any selection changes
  useEffect(() => {
    if (selectedDate && setFilter) {
      setFilter({
        day: selectedDate,
        week: "",
        month: "",
        year: "",
      });
    }
  }, [selectedDate, setFilter]);

  useEffect(() => {
    if (selectedWeek && setFilter) {
      setFilter({
        day: "",
        week: selectedWeek,
        month: "",
        year: "",
      });
    }
  }, [selectedWeek, setFilter]);

  useEffect(() => {
    if (selectedMonth && setFilter) {
      setFilter({
        day: "",
        week: "",
        month: selectedMonth,
        year: "",
      });
    }
  }, [selectedMonth, setFilter]);

  useEffect(() => {
    if (selectedYear && setFilter) {
      setFilter({
        day: "",
        week: "",
        month: "",
        year: selectedYear,
      });
    }
  }, [selectedYear, setFilter]);

  const { yDomain, yTicks } = useMemo(() => computeYAxis(data), [data]);

  // Get latest values for legend
  const lastPoint = data && data.length > 0 ? data[data.length - 1] : {};
  const latestBudget = lastPoint.budget ?? 0;
  const latestActual = lastPoint.actual ?? 0;

  // Format currency values for legend
  const formatCurrency = (value) => {
    return formatYAxisValue(value);
  };

  // Custom tick formatter for YAxis
  const yAxisTickFormatter = (value) => {
    return formatYAxisValue(value);
  };

  // Render appropriate control based on time prop
  const renderTimeControl = () => {
    if (!time) return null;

    switch (time.toLowerCase()) {
      case "day":
        return (
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
          />
        );

      case "week":
        return (
          <WeekSelector
            selectedWeek={selectedWeek}
            onWeekChange={setSelectedWeek}
          />
        );

      case "month":
        return (
          <MonthSelector
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        );

      // case "year":
      //   return <YearCalendarSelector selectedYear={selectedYear} onYearChange={setSelectedYear} />;

      default:
        return null;
    }
  };

  return (
    <div className="plc-wrapper">
      <div className="plc-card" style={{ position: "relative" }}>
        {/* Time-based control - only render if time prop is provided */}
        {time && renderTimeControl()}

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid
              vertical={false}
              strokeDasharray="6 6"
              stroke="#9aa9b7"
            />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#102a3d", fontSize: 14 }}
            />

            <YAxis
              domain={yDomain}
              ticks={yTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#102a3d", fontSize: 14 }}
              tickFormatter={yAxisTickFormatter}
            />

            {/* Tooltip for hover details */}
            <Tooltip
              cursor={{ stroke: "#e0e0e0", strokeWidth: 1 }}
              formatter={(value, name) => [
                formatYAxisValue(value),
                name === "budget" ? "Budget profit" : "Actual profit",
              ]}
              labelFormatter={(label) => label}
              contentStyle={{
                borderRadius: 10,
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                fontSize: 13,
              }}
            />

            {/* Actual Profit - Dark */}
            <Line
              type="monotone"
              dataKey="actual"
              stroke="#1a2b38"
              strokeWidth={4}
              dot={{ r: 6, fill: "#1a2b38" }}
              activeDot={{ r: 7, stroke: "#1a2b38", strokeWidth: 2 }}
            />

            {/* Budget Profit - Grey */}
            <Line
              type="monotone"
              dataKey="budget"
              stroke="#9c9c9c"
              strokeWidth={4}
              dot={{ r: 6, fill: "#9c9c9c" }}
              activeDot={{ r: 7, stroke: "#9c9c9c", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Legend */}
        <div className="plc-legend">
          <div className="plc-legend-item">
            <span className="plc-dot-actual"></span>
            <div>
              Actual profit <br />
              <strong>{formatCurrency(latestActual)}</strong>
            </div>
          </div>

          <div className="plc-legend-item">
            <span className="plc-dot-budget"></span>
            <div>
              Budget profit <br />
              <strong>{formatCurrency(latestBudget)}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLineChart;
